<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import {
  mdiUpdate,
  mdiArrowRightThin,
  mdiClock,
  mdiInformationOutline,
  mdiProgressDownload,
} from '@mdi/js'
import { useVersionStore } from '../stores/version.store'
import { vOnClickOutside } from '@vueuse/components'
import { minutesToMilliseconds } from 'date-fns'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import PrimaryButton from '@/shared/components/button/PrimaryButton.vue'
import DefaultButton from '@/shared/components/button/DefaultButton.vue'
import BaseButton from '@/shared/components/button/BaseButton.vue'
import CaretIcon from '@/shared/components/icon/CaretIcon.vue'

const versionStore = useVersionStore()
const currentVersion = ref<string>(__APP_VERSION__)
const isActive = ref<boolean>(false)
const intervalId = ref<NodeJS.Timeout>()

const setUpdateInterval = (): void => {
  if (intervalId.value) {
    clearInterval(intervalId.value)
  }
  intervalId.value = setInterval(() => versionStore.fetchVersion(), minutesToMilliseconds(3))
}

const onLoad = async (): Promise<void> => {
  await versionStore.fetchVersion()
}

onMounted(() => setUpdateInterval())

onUnmounted(() => clearInterval(intervalId.value))

const updateNow = (): void => {
  versionStore.updateMode.isDefault = false
  location.reload()
}

const updateLater = (): void => {
  isActive.value = false
}

onLoad()
</script>

<template>
  <div
    v-if="versionStore.updateMode.isDefault"
    class="relative"
    v-on-click-outside="() => (isActive = false)"
  >
    <BaseButton
      :title="$t('glossary.update')"
      class="flex items-center bg-linear-to-t from-danger/90 to-danger/60 border-2 border-danger rounded-full py-1 hover:bg-white/30"
      @click="isActive = !isActive"
    >
      <SvgIcon :path="mdiProgressDownload" class="fill-white h-5 w-5 ms-2" />
      <div class="text-sm text-white px-1.5">{{ $t('glossary.update') }}</div>
      <CaretIcon :active="isActive" class="fill-white me-2" />
    </BaseButton>
    <div
      class="z-40 absolute right-0 mt-0.5 origin-top-right border border-gray-300 bg-white transition-all transform ease-in-out duration-200 focus:outline-none text-nowrap shadow-md p-4"
      :class="isActive ? 'scale-100 opacity-100' : 'scale-75 opacity-0 collapse'"
    >
      <div class="pb-3">
        <div class="flex items-center border-b border-gray-300 pb-1">
          <SvgIcon :path="mdiInformationOutline" class="fill-gray-700 h-5 w-5 me-1" />
          <span class="font-medium text-sm">
            {{ $t('message.newVersionAvailable') }}
          </span>
        </div>
        <div class="flex items-center text-xs pt-3">
          <p>
            {{ $t('glossary.currentVersion') }}
            <span class="font-bold">{{ currentVersion }}</span>
          </p>
          <SvgIcon :path="mdiArrowRightThin" class="fill-success h-5 w-5" />
          <p>
            {{ $t('glossary.newVersion') }}
            <span class="font-bold">{{ versionStore.version }}</span>
          </p>
        </div>
      </div>
      <div class="flex items-center justify-between gap-2">
        <DefaultButton :text="$t('glossary.updateLater')" :icon="mdiClock" @click="updateLater()" />
        <PrimaryButton :text="$t('glossary.updateNow')" :icon="mdiUpdate" @click="updateNow()" />
      </div>
    </div>
  </div>
</template>
